<script lang="ts">
	import { mutation } from "@api/graphql.js";
	import sendWebMessage from "@api/mutations/sendWebMessage.graphql";
	import Button from "@components/common/Button.svelte";
	import Link from "@components/common/Link.svelte";
	import Modal from "@components/common/Modal.svelte";
	import { ButtonVariant } from "@components/common/getButtonClass.js";
	import { LinkVariant } from "@components/common/getLinkClass.js";
	import Form from "@components/form/Form.svelte";
	import LabelledFileUploader from "@components/form/LabelledFileUploader.svelte";
	import LabelledInput from "@components/form/LabelledInput.svelte";
	import LabelledSelect from "@components/form/LabelledSelect.svelte";
	import type { HTMLFormAttributes } from "svelte/elements";
	import { Locale } from "@core/schema/Locale.js";
	import type { UploadedFile } from "@core/schema/UploadedFile.js";
	import { UploadedFileBucket } from "@core/schema/UploadedFileBucket.js";
	import type { WebMessageInput } from "@core/schema/WebMessage.js";
	import { WebMessageCareerPosition } from "@core/schema/WebMessageCareerPosition.js";
	import { WebMessageType } from "@core/schema/WebMessageType.js";
	import { webMessageCareerPositionNameMap } from "@core/schema/names/webMessageCareerPositionNameMap.js";
	import { getPrivacyPolicyPath } from "@core/schema/paths/getPrivacyPolicyPath.js";
	import type { Complete } from "@core/utils/Complete.js";
	import type { Option } from "@core/utils/Option.js";
	import { isEnum } from "@core/utils/enum/isEnum.js";
	import { createPromiseWithStatus } from "@core/utils/svelte/createPromiseWithStatus.svelte.js";
	import { getGlobalSettings } from "../contexts/getGlobalSettings.js";
	import { getGlobalTemplateProps } from "../contexts/getGlobalTemplateProps.js";
	import { type CareerPosition, getVisibleCareerPositions } from "../data/careerPositions.js";

	const { position, ...rest }: Omit<HTMLFormAttributes, "onsubmit"> & { position?: CareerPosition } = $props();
	const { PUBLIC_GRAPHQL_ENDPOINT } = getGlobalSettings();
	const { tenant } = getGlobalTemplateProps();

	const texts = {
		uploadedFileTitle: {
			[Locale.cs]: "Nahraný soubor",
			[Locale.en]: "Uploaded file",
		}[tenant.locale],
		successTitle: {
			[Locale.cs]: "Děkujeme za zájem",
			[Locale.en]: "Thanks for your interest",
		}[tenant.locale],
		successBody: {
			[Locale.cs]: "Formulář byl úspěšně odeslán.",
			[Locale.en]: "Your application has been sent.",
		}[tenant.locale],
		successFollowUp: {
			[Locale.cs]: "Brzy se vám ozveme.",
			[Locale.en]: "We’ll get back to you shortly.",
		}[tenant.locale],
		acknowledge: {
			[Locale.cs]: "Rozumím",
			[Locale.en]: "Got it",
		}[tenant.locale],
		name: {
			[Locale.cs]: "Jméno a příjmení",
			[Locale.en]: "Full name",
		}[tenant.locale],
		email: {
			[Locale.cs]: "E-mail",
			[Locale.en]: "E-mail",
		}[tenant.locale],
		phoneNumber: {
			[Locale.cs]: "Telefonní číslo",
			[Locale.en]: "Phone number",
		}[tenant.locale],
		positionLabel: {
			[Locale.cs]: "Pozice",
			[Locale.en]: "Position",
		}[tenant.locale],
		positionPlaceholder: {
			[Locale.cs]: "-- Vyberte --",
			[Locale.en]: "-- Select --",
		}[tenant.locale],
		attachment: {
			[Locale.cs]: "Přilož CV nebo pošli odkaz na tvůj LinkedIn",
			[Locale.en]: "Attach your CV or send a link to your LinkedIn",
		}[tenant.locale],
		linkedInUrl: {
			[Locale.cs]: "URL na LinkedIn",
			[Locale.en]: "LinkedIn URL",
		}[tenant.locale],
		gdprConsent: {
			[Locale.cs]: "Souhlasím se",
			[Locale.en]: "I agree to the",
		}[tenant.locale],
		gdprLink: {
			[Locale.cs]: "Zásadami zpracování osobních údajů",
			[Locale.en]: "Personal data processing policy",
		}[tenant.locale],
		sending: {
			[Locale.cs]: "Odesílání…",
			[Locale.en]: "Sending…",
		}[tenant.locale],
		submit: {
			[Locale.cs]: "Odeslat",
			[Locale.en]: "Send",
		}[tenant.locale],
		error: {
			[Locale.cs]: "Omlouváme se, ale při odesílání formuláře došlo k chybě. Zkuste to prosím znovu nebo později.",
			[Locale.en]: "Sorry, something went wrong while sending the form. Please try again later.",
		}[tenant.locale],
	};

	let showSuccessModal = $state<boolean>(false);
	let errorHappened = $state<boolean>(false);
	let gdprApprovedAt = $state<string | null>(null);
	let attachment = $state<UploadedFile | undefined>(undefined);

	function onGdprToggle(event: Event): void {
		const input = event.target as HTMLInputElement;
		gdprApprovedAt = input.checked ? new Date().toISOString() : null;
	}

	const send = createPromiseWithStatus(async (input: Omit<WebMessageInput, "contactDetail">) =>
		mutation<boolean, { input: typeof input }>(PUBLIC_GRAPHQL_ENDPOINT, sendWebMessage, { input }),
	);

	const isWebMessageCareerPosition = isEnum(WebMessageCareerPosition);

	async function submit(event: SubmitEvent): Promise<void> {
		event.preventDefault();
		if (!gdprApprovedAt) {
			throw new Error("GDPR must be approved.");
		}

		errorHappened = false;

		const form = event.target as HTMLFormElement;
		const formData = new FormData(form);
		const name = formData.get("name") as string;
		const phoneNumber = formData.get("phoneNumber") as string;
		const email = formData.get("email") as string;
		const linkedInUrl = (formData.get("linkedInUrl") as string | null) ?? undefined;
		const position = (formData.get("position") as string | null) ?? undefined;
		if (!isWebMessageCareerPosition(position)) {
			throw new Error("Invalid position value");
		}

		try {
			const input: Complete<Omit<WebMessageInput, "contactDetail">> = {
				name,
				phoneNumber,
				email,
				type: WebMessageType.Career,
				gdprApprovedAt,
				careerDetail: {
					attachment: attachment && {
						...attachment,
						title: attachment.title ?? texts.uploadedFileTitle,
					},
					linkedInUrl: linkedInUrl ? linkedInUrl : undefined,
					position,
				},
				locale: tenant.locale,
			};
			await send.call(input);
			showSuccessModal = true;
			form.reset();
			// eslint-disable-next-line require-atomic-updates
			attachment = undefined;
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(error);
			errorHappened = true;
		}
	}

	const availablePositionOptions = $derived.by<Option[]>(() => {
		const availablePositions = [
			...new Set(getVisibleCareerPositions(tenant.locale).map(({ position }: CareerPosition) => position)),
		];
		return availablePositions.map((position: WebMessageCareerPosition) => ({
			value: position,
			label: webMessageCareerPositionNameMap[position][tenant.locale],
		}));
	});
</script>

<Modal show={showSuccessModal}>
	<p class="text-xl text-fstonyx">{texts.successTitle}</p>
	<div class="text-center flex flex-col gap-8">
		<p>
			{texts.successBody}
			<br />
			{texts.successFollowUp}
		</p>

		<Button
			variant={ButtonVariant.Phlox}
			class="ml-auto"
			onclick={(): void => {
				showSuccessModal = false;
			}}
		>
			{texts.acknowledge}
		</Button>
	</div>
</Modal>

<div class="flex flex-col items-center gap-8">
	<Form {...rest} onsubmit={submit} enableRecaptcha class={["grid grid-cols-1 lg:grid-cols-2 gap-4", rest.class]}>
		<LabelledInput name="name" required>{texts.name}</LabelledInput>
		<LabelledInput type="email" name="email" required>{texts.email}</LabelledInput>
		<LabelledInput name="phoneNumber" type="tel" required>{texts.phoneNumber}</LabelledInput>
		<LabelledSelect name="position" required>
			{texts.positionLabel}

			{#snippet options()}
				<option value="" disabled selected>{texts.positionPlaceholder}</option>
				{#each availablePositionOptions as { value, label } (value)}
					<option {value} selected={position?.position === (value as WebMessageCareerPosition)}>{label}</option>
				{/each}
			{/snippet}
		</LabelledSelect>

		<LabelledFileUploader
			wrapperClass="col-span-full"
			destinationFolder="careerFormAttachment"
			bucket={UploadedFileBucket.Upload}
			currentFiles={attachment ? [attachment] : []}
			onchange={(file: UploadedFile | undefined): void => {
				attachment = file;
			}}
		>
			{texts.attachment}
		</LabelledFileUploader>

		<LabelledInput type="url" name="linkedInUrl" wrapperClass="col-span-full">{texts.linkedInUrl}</LabelledInput>

		<LabelledInput type="checkbox" wrapperClass="col-span-full" onchange={onGdprToggle} required>
			{texts.gdprConsent}
			<Link variant={LinkVariant.Underline} href={getPrivacyPolicyPath(tenant)}>
				{texts.gdprLink}
			</Link>
		</LabelledInput>

		<Button
			variant={ButtonVariant.Green}
			disabled={send.value.status === "loading"}
			class="place-self-center col-span-full"
		>
			{#if send.value.status === "loading"}
				{texts.sending}
			{:else}
				{texts.submit}
			{/if}
		</Button>
	</Form>

	{#if errorHappened}
		<p class="text-fstphlox">
			{texts.error}
		</p>
	{/if}
</div>
